import { useMemo, useState } from "react";
import {
  ArrowDownToLine,
  Download,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { AxiosResponse } from "axios";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { useDownloadExport, useListExports } from "../../../hooks/QueryHooks";
import { ExportResponse, ListExportsResponse } from "../../../services/exports/exportsEndpoints.interface";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import notify from "../../../utils/notify";

type ExportStatus = "completed" | "processing" | "failed" | "pending" | "queued" | "in_progress" | "unknown";

type StatusFilter = "all" | ExportStatus;

const normalizeStatus = (value?: string): ExportStatus => {
  const status = String(value || "").toLowerCase();
  if (["completed"].includes(status)) return "completed";
  if (["processing", "in_progress"].includes(status)) return "processing";
  if (["pending"].includes(status)) return "pending";
  if (["queued"].includes(status)) return "queued";
  if (["failed", "error"].includes(status)) return "failed";
  return "unknown";
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return value;
  }
};

const statusPill = (status: ExportStatus) => {
  if (status === "completed") {
    return <Badge variant="outline" className="border-status-active/30 bg-status-active/10 text-status-active">Completed</Badge>;
  }
  if (status === "processing") {
    return <Badge variant="outline" className="border-status-info/30 bg-status-info/10 text-status-info">In Progress</Badge>;
  }
  if (status === "queued") {
    return <Badge variant="outline" className="border-border-chip bg-muted text-muted-foreground">Queued</Badge>;
  }
  if (status === "pending") {
    return <Badge variant="outline" className="border-border-chip bg-muted text-muted-foreground">Pending</Badge>;
  }
  if (status === "failed") {
    return <Badge variant="outline" className="border-destructive/30 bg-destructive/10 text-destructive">Failed</Badge>;
  }

  return <Badge variant="outline" className="border-border-chip bg-muted text-muted-foreground">Unknown</Badge>;
};

const ExportsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [refreshing, setRefreshing] = useState(false);

  const exportsQuery = useListExports({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });
  const exportsResponse = exportsQuery?.data as ListExportsResponse | undefined;

  const downloadExportMutation = useDownloadExport();

  const exports = useMemo(() => {
    const source = exportsResponse?.items || [];

    return source.filter((item: ExportResponse) => {
      const normalizedStatus = normalizeStatus(item.status);
      const matchesStatus = statusFilter === "all" || normalizedStatus === statusFilter;
      const q = searchValue.trim().toLowerCase();
      const matchesSearch = !q
        || String(item?.view?.view_name || "").toLowerCase().includes(q)
        || String(item?.view?.entity_relation || "").toLowerCase().includes(q)
        || String(item?.status || "").toLowerCase().includes(q);

      return matchesStatus && matchesSearch;
    });
  }, [exportsResponse?.items, searchValue, statusFilter]);

  const counts = useMemo(() => {
    const source = exportsResponse?.items || [];
    const byStatus: Record<ExportStatus, number> = {
      completed: 0,
      processing: 0,
      failed: 0,
      pending: 0,
      queued: 0,
      in_progress: 0,
      unknown: 0,
    };

    source.forEach((item: ExportResponse) => {
      const status = normalizeStatus(item.status);
      byStatus[status] = (byStatus[status] || 0) + 1;
    });

    return {
      total: source.length,
      completed: byStatus.completed,
      processing: byStatus.processing + byStatus.queued + byStatus.pending,
    };
  }, [exportsResponse?.items]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await exportsQuery.refetch();
    } finally {
      setTimeout(() => setRefreshing(false), 250);
    }
  };

  const handleDownload = (item: ExportResponse) => {
    if (!workspace?.id) return;

    if (item?.download_url) {
      window.open(item.download_url, "_blank");
      notify({ type: "success", messageList: ["Download started in a new tab"] });
      return;
    }

    downloadExportMutation.mutate(
      {
        workspace_id: workspace.id,
        id: item.id,
      },
      {
        onSuccess: (response: AxiosResponse<string>) => {
          const url = response?.data;
          if (!url) {
            notify({ type: "failure", messageList: ["No download URL received from server"] });
            return;
          }
          window.open(url, "_blank");
          notify({ type: "success", messageList: ["Download started in a new tab"] });
        },
        onError: (error: any) => {
          notify({
            type: "failure",
            messageList: [error?.response?.data?.message || "Unable to download export"],
          });
        },
      },
    );
  };

  if (exportsQuery?.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="mx-auto max-w-5xl px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Exports</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">All data exports from your workspace</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => { handleRefresh().catch(() => {}); }} disabled={refreshing}>
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Refresh
            </Button>
            <Button size="sm" className="h-8 gap-1.5" disabled>
              <Plus className="h-3.5 w-3.5" />
              New Export
            </Button>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-4">
          <div className="flex items-center gap-2 text-[13px]">
            <span className="text-muted-foreground">Total:</span>
            <span className="font-medium text-foreground">{counts.total}</span>
          </div>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2 text-[13px]">
            <div className="h-1.5 w-1.5 rounded-full bg-status-active" />
            <span className="text-muted-foreground">{counts.completed} completed</span>
          </div>
          {counts.processing > 0 && (
            <>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-2 text-[13px]">
                <div className="h-1.5 w-1.5 rounded-full bg-status-info" />
                <span className="text-muted-foreground">{counts.processing} in progress</span>
              </div>
            </>
          )}
        </div>

        <div className="mb-5 flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search exports..."
              value={searchValue}
              onChange={(event) => setSearchValue(event.currentTarget.value)}
              className="h-8 pl-8 text-[13px]"
            />
          </div>

          <div className="flex items-center gap-1">
            {([
              ["all", "All"],
              ["completed", "Completed"],
              ["processing", "In Progress"],
              ["pending", "Pending"],
              ["queued", "Queued"],
              ["failed", "Failed"],
            ] as Array<[StatusFilter, string]>).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={
                  statusFilter === value
                    ? "rounded-[5px] bg-foreground px-2.5 py-1 text-[12px] font-medium text-background"
                    : "rounded-[5px] px-2.5 py-1 text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border">
          <div className="grid grid-cols-[1fr_110px_120px_120px_120px_44px] gap-3 border-b border-border bg-muted/50 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <span>Export</span>
            <span>Object</span>
            <span>Status</span>
            <span>Created</span>
            <span>Completed</span>
            <span />
          </div>

          {exports.map((item: ExportResponse) => {
            const status = normalizeStatus(item.status);
            const canDownload = status === "completed";

            return (
              <div
                key={item.id}
                className="grid grid-cols-[1fr_110px_120px_120px_120px_44px] items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 hover:bg-muted/40"
              >
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-medium text-foreground">{item?.view?.view_name || "Unnamed export"}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{item?.view?.view_description || "No description"}</p>
                </div>

                <span className="text-[13px] text-muted-foreground">{item?.view?.entity_relation || "-"}</span>
                <span>{statusPill(status)}</span>
                <span className="text-[12px] text-muted-foreground">{formatDate(item.created_at)}</span>
                <span className="text-[12px] text-muted-foreground">{formatDate(item.completed_at)}</span>

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => (canDownload ? handleDownload(item) : undefined)}
                    disabled={!canDownload}
                    aria-label={canDownload ? "Download export" : "Export not ready"}
                  >
                    {canDownload ? <ArrowDownToLine className="h-3.5 w-3.5" /> : <MoreHorizontal className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {exports.length === 0 && (
          <div className="mt-5 rounded-lg border border-border py-16 text-center">
            <Download className="mx-auto mb-3 h-8 w-8 text-muted-foreground/30" />
            <p className="text-[13px] text-muted-foreground">No exports found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportsPage;
